import { Skeleton } from "antd";

/**
 * Predefined width sizes for loading title skeletons.
 * Used to maintain consistent sizing across the application.
 */
export const LoadingTitleSize = {
    /** Large title width (450px) - typically for main page headers */
    LARGE: 450,
    /** Medium title width (300px) - default size for most titles */
    MEDIUM: 300,
    /** Saved view width (215px) - specific to saved view components */
    SAVED_VIEW: 215,
    /** Small title width (150px) - for compact sections */
    SMALL: 150,
    /** Extra small width (50px) - for minimal loading indicators */
    XSMALL: 50,
} as const;

type LoadingTitleSizeType = (typeof LoadingTitleSize)[keyof typeof LoadingTitleSize];

/**
 * Props for the RVLoadingTitle component.
 */
type RVLoadingTitleProps = {
    /**
     * Width of the loading title skeleton.
     * Can be a predefined size from LoadingTitleSize or a custom string (e.g., "200px", "50%").
     * @default LoadingTitleSize.MEDIUM (300px)
     */
    size?: LoadingTitleSizeType | string;
};

/**
 * A loading skeleton component for title placeholders.
 *
 * Displays an animated skeleton with configurable width while content is loading.
 * Uses Ant Design's Skeleton component with title-only configuration.
 *
 * @example
 * ```tsx
 * // Using predefined size
 * <RVLoadingTitle size={LoadingTitleSize.LARGE} />
 *
 * // Using custom size
 * <RVLoadingTitle size="400px" />
 *
 * // Using default size
 * <RVLoadingTitle />
 * ```
 */
export function RVLoadingTitle({ size = LoadingTitleSize.MEDIUM }: RVLoadingTitleProps): JSX.Element {
    return <Skeleton active paragraph={false} title={{ width: size }} />;
}
